import { useState } from 'react';

import { Button } from '@/_shared/components/ui/button';
import { Checkbox } from '@/_shared/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';
import { Label } from '@/_shared/components/ui/label';

export interface RemoveTarget {
  userId: string;
  name: string;
  rsAdmin: boolean;
}

interface RemoveSiblingMemberDialogProps {
  target: RemoveTarget | null;
  pending: boolean;
  onConfirm: (input: { userId: string; removeFromOrganization: boolean }) => void;
  onClose: () => void;
}

/**
 * Remove (LLD §5.6). For a person who is not a RudderStack admin, "Also remove from the organization" is on by
 * default. A RudderStack admin has no sibling membership, so only the Lookout membership goes and the option is hidden.
 */
export function RemoveSiblingMemberDialog({
  target,
  pending,
  onConfirm,
  onClose,
}: RemoveSiblingMemberDialogProps): React.ReactElement {
  const [removeFromOrganization, setRemoveFromOrganization] = useState(true);

  const close = (): void => {
    setRemoveFromOrganization(true);
    onClose();
  };

  let description = '';
  if (target?.rsAdmin) {
    description = `${target.name} leaves this Lookout workspace. They stay a RudderStack admin of the organization.`;
  } else if (target) {
    description = removeFromOrganization
      ? `${target.name} leaves this Lookout workspace and the RudderStack organization, with all its workspaces.`
      : `${target.name} leaves this Lookout workspace and its RudderStack workspace, and stays in the organization.`;
  }

  return (
    <Dialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open && !pending) close();
      }}
    >
      <DialogContent
        onEscapeKeyDown={(event) => {
          if (pending) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (pending) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Remove member?</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {target && !target.rsAdmin ? (
          <div className="flex items-center gap-2">
            <Checkbox
              id="remove-from-organization"
              checked={removeFromOrganization}
              disabled={pending}
              onCheckedChange={(checked) => setRemoveFromOrganization(checked === true)}
            />
            <Label htmlFor="remove-from-organization">Also remove from the organization</Label>
          </div>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="secondary" disabled={pending} onClick={close} label="Cancel" />
          <Button
            type="button"
            variant="primary-destructive"
            loading={pending}
            onClick={() =>
              target &&
              onConfirm({ userId: target.userId, removeFromOrganization: !target.rsAdmin && removeFromOrganization })
            }
            label="Remove"
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
